import { useQuery } from "@tanstack/react-query";
import { useState, useCallback } from "react";
// Dynamic import to avoid initialization order issues
import { Category } from "../types";

// Hook to get categories by parent ID
export const useCategoriesByParent = (
  parentId: string,
  limitCount?: number
) => {
  return useQuery({
    queryKey: ["categories", "byParent", parentId, limitCount],
    queryFn: async () => {
      const { categoryService } = await import("../services/firebaseConfig");
      return await categoryService.getCategoriesByParent(parentId, limitCount);
    },
    enabled: !!parentId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

// Hook to get top-level categories with localStorage caching for instant load
export const useTopLevelCategories = () => {
  return useQuery({
    queryKey: ["categories", "topLevel"],
    queryFn: async () => {
      // Check localStorage first for instant load
      const cacheKey = "categories_topLevel";
      const cached = localStorage.getItem(cacheKey);

      if (cached) {
        try {
          const { data, timestamp } = JSON.parse(cached);
          // Use cache if less than 30 minutes old (categories rarely change)
          if (Date.now() - timestamp < 30 * 60 * 1000) {
            // Return cached data immediately, but still fetch fresh in background
            return data;
          }
        } catch (e) {
          // Invalid cache, continue to fetch
        }
      }

      const { categoryService } = await import("../services/firebaseConfig");
      try {
        const categories = await categoryService.getTopLevelCategories();

        // Cache in localStorage for next time
        try {
          localStorage.setItem(
            cacheKey,
            JSON.stringify({
              data: categories,
              timestamp: Date.now(),
            })
          );
        } catch (e) {
          // localStorage might be full or disabled, continue silently
        }

        return categories;
      } catch (error: any) {
        // If fetch fails, try to return cached data as fallback
        if (cached) {
          try {
            const { data, timestamp } = JSON.parse(cached);
            // Use cache even if expired as fallback
            if (data && Array.isArray(data)) {
              return data;
            }
          } catch (e) {
            // Invalid cache, throw original error
          }
        }
        // Re-throw error if no cache available
        throw error;
      }
    },
    staleTime: 30 * 60 * 1000, // 30 minutes (categories rarely change)
    gcTime: 60 * 60 * 1000, // 1 hour
    refetchOnMount: false, // Don't refetch if we have cached data
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    // Use cached data as placeholder for instant UI
    placeholderData: (previousData) => {
      if (previousData) return previousData;

      // Try to get from localStorage as placeholder
      try {
        const cached = localStorage.getItem("categories_topLevel");
        if (cached) {
          const { data, timestamp } = JSON.parse(cached);
          if (Date.now() - timestamp < 30 * 60 * 1000) {
            return data;
          }
        }
      } catch (e) {
        // Ignore errors
      }

      return undefined;
    },
  });
};

// Hook to get category by ID
export const useCategoryById = (id: string) => {
  return useQuery({
    queryKey: ["categories", "byId", id],
    queryFn: async () => {
      const { categoryService } = await import("../services/firebaseConfig");
      return await categoryService.getCategoryByIdNew(id);
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Hook to get breadcrumb trail for a category
export const useCategoryBreadcrumb = (id: string) => {
  return useQuery({
    queryKey: ["categories", "breadcrumb", id],
    queryFn: async () => {
      const { categoryService } = await import("../services/firebaseConfig");
      return await categoryService.getBreadcrumbTrail(id);
    },
    enabled: !!id, // Only run query if id is provided
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Hook to get category hierarchy (category + children)
export const useCategoryHierarchy = (categoryId: string) => {
  return useQuery({
    queryKey: ["categories", "hierarchy", categoryId],
    queryFn: async () => {
      const { categoryService } = await import("../services/firebaseConfig");
      if (!categoryId) {
        // Return top-level categories
        return await categoryService.getTopLevelCategories();
      }
      // Get the category and its children
      const category = await categoryService.getCategoryByIdNew(categoryId);
      const children = await categoryService.getCategoriesByParent(categoryId);

      return {
        category,
        children,
        breadcrumb: category
          ? await categoryService.getBreadcrumbTrail(categoryId)
          : [],
      };
    },
    enabled: !!categoryId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Hook to get category path (breadcrumb + category + children)
export const useCategoryPath = (categoryId: string) => {
  return useQuery({
    queryKey: ["categories", "path", categoryId],
    queryFn: async () => {
      const { categoryService } = await import("../services/firebaseConfig");
      const breadcrumb = await categoryService.getBreadcrumbTrail(categoryId);
      const category = await categoryService.getCategoryByIdNew(categoryId);
      const children = await categoryService.getCategoriesByParent(categoryId);

      return {
        breadcrumb,
        category,
        children,
      };
    },
    enabled: !!categoryId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Hook to get categories by level (0 = top level, 1 = first level children, etc.)
export const useCategoriesByLevel = (level: number) => {
  return useQuery({
    queryKey: ["categories", "byLevel", level],
    queryFn: async () => {
      const { categoryService } = await import("../services/firebaseConfig");
      if (level === 0) {
        return await categoryService.getTopLevelCategories();
      }
      // For deeper levels, we need to traverse the hierarchy
      // This is a simplified approach - in practice, you might want to cache this
      const topLevel = await categoryService.getTopLevelCategories();
      const allCategories: Category[] = [];

      const traverseLevel = async (
        categories: Category[],
        currentLevel: number
      ) => {
        if (currentLevel === level) {
          allCategories.push(...categories);
          return;
        }

        for (const category of categories) {
          const children = await categoryService.getCategoriesByParent(
            category.id
          );
          if (children.length > 0) {
            await traverseLevel(children, currentLevel + 1);
          }
        }
      };

      await traverseLevel(topLevel, 0);
      return allCategories;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Hook to get category tree (hierarchical structure)
export const useCategoryTree = () => {
  return useQuery({
    queryKey: ["categories", "tree"],
    queryFn: async () => {
      const { categoryService } = await import("../services/firebaseConfig");
      const topLevel = await categoryService.getTopLevelCategories();

      const buildTree = async (categories: Category[]): Promise<any[]> => {
        const tree = [];

        for (const category of categories) {
          const children = await categoryService.getCategoriesByParent(
            category.id
          );
          const node = {
            ...category,
            children: children.length > 0 ? await buildTree(children) : [],
          };
          tree.push(node);
        }

        return tree;
      };

      return await buildTree(topLevel);
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Hook to get sibling categories
export const useCategorySiblings = (categoryId: string) => {
  return useQuery({
    queryKey: ["categories", "siblings", categoryId],
    queryFn: async () => {
      const { categoryService } = await import("../services/firebaseConfig");
      const category = await categoryService.getCategoryByIdNew(categoryId);
      if (!category) return [];

      return await categoryService.getCategoriesByParent(category.parentId);
    },
    enabled: !!categoryId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Hook to get root categories (categories where parentId == "" and isActive == true)
export const useRootCategories = () => {
  return useQuery({
    queryKey: ["categories", "root"],
    queryFn: async () => {
      const { categoryService } = await import("../services/firebaseConfig");
      return await categoryService.getRootCategories();
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Hook to get subcategories (categories where parentId == parentId and isActive == true)
export const useSubcategories = (parentId: string) => {
  return useQuery({
    queryKey: ["categories", "subcategories", parentId],
    queryFn: async () => {
      const { categoryService } = await import("../services/firebaseConfig");
      return await categoryService.getSubcategories(parentId);
    },
    enabled: !!parentId, // Only run if parentId is defined and non-empty
    staleTime: 10 * 60 * 1000, // 10 minutes - categories don't change often
    gcTime: 30 * 60 * 1000, // 30 minutes - keep in cache longer
    refetchOnWindowFocus: false, // Don't refetch on window focus
    refetchOnMount: false, // Don't refetch when component remounts if data exists
  });
};

// Hook to get categories with product counts
export const useCategoriesWithProductCounts = (parentId?: string) => {
  return useQuery({
    queryKey: ["categories", "withProductCounts", parentId],
    queryFn: async () => {
      const { categoryService } = await import("../services/firebaseConfig");
      return await categoryService.getCategoriesWithProductCounts(parentId);
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Hook to get complete category hierarchy with batched fetching
export const useCompleteCategoryHierarchy = () => {
  return useQuery({
    queryKey: ["categories", "completeHierarchy"],
    queryFn: async () => {
      // Check localStorage first for instant load
      const cacheKey = "categories_completeHierarchy";
      const cached = localStorage.getItem(cacheKey);

      if (cached) {
        try {
          const { data, timestamp } = JSON.parse(cached);
          // Use cache if less than 30 minutes old
          if (Date.now() - timestamp < 30 * 60 * 1000) {
            return data;
          }
        } catch (e) {
          // Invalid cache, continue to fetch
        }
      }

      const { categoryService } = await import("../services/firebaseConfig");

      try {
        // Get all top-level categories first
        const topLevelCategories = await categoryService.getTopLevelCategories();

        // Build complete hierarchy with parallel fetching
        const hierarchy: Map<string, Category[]> = new Map();

        // Function to fetch all descendants in parallel
        const fetchDescendantsParallel = async (categories: Category[]): Promise<void> => {
          const fetchPromises = categories.map(async (category) => {
            const children = await categoryService.getCategoriesByParent(category.id);
            if (children.length > 0) {
              hierarchy.set(category.id, children);
              // Recursively fetch grandchildren in parallel
              await fetchDescendantsParallel(children);
            }
          });

          await Promise.all(fetchPromises);
        };

        // Start with top-level categories
        hierarchy.set("fashion", topLevelCategories);
        await fetchDescendantsParallel(topLevelCategories);

        // Build flattened maps for quick lookups
        const categoryMap = new Map<string, string>(); // categoryId -> primaryCategoryName
        const parentMap = new Map<string, string>(); // categoryId -> parentId
        const allCategories = new Map<string, Category>(); // categoryId -> Category

        // Helper to traverse and build maps
        const buildMaps = (categories: Category[], primaryName: string, parentId: string = "") => {
          categories.forEach(category => {
            categoryMap.set(category.id, primaryName);
            parentMap.set(category.id, parentId);
            allCategories.set(category.id, category);

            const children = hierarchy.get(category.id);
            if (children) {
              buildMaps(children, primaryName, category.id);
            }
          });
        };

        buildMaps(topLevelCategories, "");

        const result = {
          hierarchy: Object.fromEntries(hierarchy), // Convert Map to object for serialization
          categoryMap: Object.fromEntries(categoryMap),
          parentMap: Object.fromEntries(parentMap),
          allCategories: Object.fromEntries(allCategories),
          topLevelCategories,
        };

        // Cache in localStorage
        try {
          localStorage.setItem(
            cacheKey,
            JSON.stringify({
              data: result,
              timestamp: Date.now(),
            })
          );
        } catch (e) {
          // localStorage might be full or disabled, continue silently
        }

        return result;
      } catch (error: any) {
        // If fetch fails, try to return cached data as fallback
        if (cached) {
          try {
            const { data } = JSON.parse(cached);
            if (data && data.hierarchy) {
              return data;
            }
          } catch (e) {
            // Invalid cache, throw original error
          }
        }
        throw error;
      }
    },
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    placeholderData: (previousData) => {
      if (previousData) return previousData;

      // Try to get from localStorage as placeholder
      try {
        const cached = localStorage.getItem("categories_completeHierarchy");
        if (cached) {
          const { data, timestamp } = JSON.parse(cached);
          if (Date.now() - timestamp < 30 * 60 * 1000) {
            return data;
          }
        }
      } catch (e) {
        // Ignore errors
      }

      return undefined;
    },
  });
};

// Hook for simplified category selection state management
export const useCategorySelection = () => {
  const [selection, setSelection] = useState<{
    mode: 'primary' | 'subcategory' | 'none';
    primaryCategoryId: string | null;
    subcategoryId: string | null;
  }>({
    mode: 'none',
    primaryCategoryId: null,
    subcategoryId: null,
  });

  const selectPrimaryCategory = useCallback((categoryId: string) => {
    setSelection({
      mode: 'primary',
      primaryCategoryId: categoryId,
      subcategoryId: null,
    });
  }, []);

  const selectSubcategory = useCallback((subcategoryId: string, primaryCategoryId: string) => {
    setSelection({
      mode: 'subcategory',
      primaryCategoryId,
      subcategoryId,
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelection({
      mode: 'none',
      primaryCategoryId: null,
      subcategoryId: null,
    });
  }, []);

  const getSelectedCategoryIds = useCallback(() => {
    switch (selection.mode) {
      case 'primary':
        return selection.primaryCategoryId ? [selection.primaryCategoryId] : [];
      case 'subcategory':
        return selection.subcategoryId ? [selection.subcategoryId] : [];
      default:
        return [];
    }
  }, [selection]);

  const getPrimaryCategoryName = useCallback(() => {
    return selection.primaryCategoryId;
  }, [selection.primaryCategoryId]);

  return {
    selection,
    selectPrimaryCategory,
    selectSubcategory,
    clearSelection,
    getSelectedCategoryIds,
    getPrimaryCategoryName,
  };
};

// Hook to fetch main categories with root fallback
// This hook intelligently fetches main-level categories (parentId="fashion")
// If no main-level categories exist, it falls back to root-level categories (parentId="")
export const useMainCategories = () => {
  return useQuery({
    queryKey: ["categories", "mainWithFallback"],
    queryFn: async () => {
      const { categoryService } = await import("../services/firebaseConfig");
      
      // First, try to fetch main-level categories (fashion children)
      const mainCategories = await categoryService.getCategoriesByParent("fashion");
      
      // If main categories exist, return them
      if (mainCategories && mainCategories.length > 0) {
        return {
          categories: mainCategories,
          level: "main" as const,
          parentId: "fashion",
        };
      }
      
      // Otherwise, fallback to root-level categories
      const rootCategories = await categoryService.getCategoriesByParent("");
      
      return {
        categories: rootCategories,
        level: "root" as const,
        parentId: "",
      };
    },
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

// Hook to intelligently determine category display information
// Returns category with proper hierarchy context and fallback logic
export const useSmartCategoryDisplay = (categoryId: string | null) => {
  return useQuery({
    queryKey: ["categories", "smartDisplay", categoryId],
    queryFn: async () => {
      if (!categoryId) return null;
      
      const { categoryService } = await import("../services/firebaseConfig");
      
      // Fetch the category
      const category = await categoryService.getCategoryByIdNew(categoryId);
      if (!category) return null;
      
      // Determine hierarchy level
      let hierarchyLevel: "root" | "main" | "sub";
      let displayName = category.name;
      let parentCategory: Category | null = null;
      
      if (category.parentId === "") {
        hierarchyLevel = "root";
      } else if (category.parentId === "fashion") {
        hierarchyLevel = "main";
      } else {
        hierarchyLevel = "sub";
        // For subcategories, fetch parent for context
        try {
          parentCategory = await categoryService.getCategoryByIdNew(category.parentId);
          
          // If parent not found or inactive, try to find its root-level parent
          if (!parentCategory || !parentCategory.isActive) {
            const breadcrumb = await categoryService.getBreadcrumbTrail(categoryId);
            if (breadcrumb.length > 0) {
              // Use the first breadcrumb item as root fallback
              parentCategory = breadcrumb[0];
              displayName = `${breadcrumb[0].name} - ${category.name}`;
            }
          }
        } catch (error) {
          console.warn("Failed to fetch parent category:", error);
        }
      }
      
      return {
        category,
        hierarchyLevel,
        displayName,
        parentCategory,
        shouldShowRootFallback: !parentCategory && hierarchyLevel === "sub",
      };
    },
    enabled: !!categoryId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Enhanced category selection hook with hierarchy tracking and descendant fetching
export const useEnhancedCategorySelection = () => {
  const [selection, setSelection] = useState<{
    mode: 'primary' | 'subcategory' | 'none';
    hierarchyLevel: 'root' | 'main' | 'sub' | null;
    primaryCategoryId: string | null;
    subcategoryId: string | null;
  }>({
    mode: 'none',
    hierarchyLevel: null,
    primaryCategoryId: null,
    subcategoryId: null,
  });

  const selectPrimaryCategory = useCallback(async (categoryId: string) => {
    // Determine hierarchy level
    const { categoryService } = await import("../services/firebaseConfig");
    const category = await categoryService.getCategoryByIdNew(categoryId);
    
    let hierarchyLevel: 'root' | 'main' | 'sub' = 'main';
    if (category) {
      if (category.parentId === "") {
        hierarchyLevel = 'root';
      } else if (category.parentId === "fashion") {
        hierarchyLevel = 'main';
      } else {
        hierarchyLevel = 'sub';
      }
    }
    
    setSelection({
      mode: 'primary',
      hierarchyLevel,
      primaryCategoryId: categoryId,
      subcategoryId: null,
    });
  }, []);

  const selectSubcategory = useCallback((subcategoryId: string, primaryCategoryId: string) => {
    setSelection({
      mode: 'subcategory',
      hierarchyLevel: 'sub',
      primaryCategoryId,
      subcategoryId,
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelection({
      mode: 'none',
      hierarchyLevel: null,
      primaryCategoryId: null,
      subcategoryId: null,
    });
  }, []);

  // Enhanced method that returns category with all descendants for comprehensive filtering
  const getSelectedCategoryIdsWithDescendants = useCallback(async () => {
    if (selection.mode === 'none') return [];
    
    const { categoryService } = await import("../services/firebaseConfig");
    
    // If subcategory is selected, return it and all its descendants
    if (selection.mode === 'subcategory' && selection.subcategoryId) {
      try {
        const descendantIds = await categoryService.getAllCategoryIds(selection.subcategoryId);
        return descendantIds;
      } catch (error) {
        console.warn("Failed to fetch descendants:", error);
        return [selection.subcategoryId];
      }
    }
    
    // If primary category is selected, return it and all its descendants
    if (selection.mode === 'primary' && selection.primaryCategoryId) {
      try {
        const descendantIds = await categoryService.getAllCategoryIds(selection.primaryCategoryId);
        return descendantIds;
      } catch (error) {
        console.warn("Failed to fetch descendants:", error);
        return [selection.primaryCategoryId];
      }
    }
    
    return [];
  }, [selection]);

  const getSelectedCategoryIds = useCallback(() => {
    switch (selection.mode) {
      case 'primary':
        return selection.primaryCategoryId ? [selection.primaryCategoryId] : [];
      case 'subcategory':
        return selection.subcategoryId ? [selection.subcategoryId] : [];
      default:
        return [];
    }
  }, [selection]);

  const getPrimaryCategoryName = useCallback(() => {
    return selection.primaryCategoryId;
  }, [selection.primaryCategoryId]);

  return {
    selection,
    selectPrimaryCategory,
    selectSubcategory,
    clearSelection,
    getSelectedCategoryIds,
    getSelectedCategoryIdsWithDescendants,
    getPrimaryCategoryName,
  };
};
